import {
  isValidDate,
  MASK_CHAR_FOCUSED,
  MASK_CHAR_UNFOCUSED,
  MASK_EMPTY,
} from 'app/components/Core/ChakraDatePicker/utils';
import { InputField } from 'app/components/Core/ChakraInputField';
import { Icon } from '@chakra-ui/react';
import { ReactComponent as CloseIcon } from 'resources/images/close.svg';
import { ReactComponent as CalendarIconSvg } from 'resources/images/calendar.svg';
import React from 'react';
import InputMask from 'react-input-mask';
import { ControllerRenderProps, Path } from 'react-hook-form';
import { HelpTooltip } from 'app/components/HelpTooltip';

interface DateFieldInputMaskProps<T> {
  shouldShowFocusedMask: boolean;
  maskValue: string;
  shouldSaveInvalidDate?: boolean;
  field: ControllerRenderProps<T, Path<T>>;
  setMaskValue: (value: string) => void;
  label?: string;
  placeholder?: string;
  dataTestId?: string;
  errorMessage?: string;
  isRequired?: boolean;
  setIsOpen: () => void;
  name: string;
  iconRef: React.RefObject<SVGSVGElement>;
  tooltip?: string;
}

const DEFAULT_MASK_FOCUSED = '9999-99-99';

export const DateFieldInputMask = <T extends {}>({
  maskValue,
  shouldShowFocusedMask,
  shouldSaveInvalidDate,
  field,
  setMaskValue,
  label,
  placeholder,
  dataTestId,
  errorMessage,
  isRequired,
  setIsOpen,
  name,
  iconRef,
  tooltip,
}: DateFieldInputMaskProps<T>) => {
  return (
    <InputMask
      mask={
        shouldShowFocusedMask
          ? DEFAULT_MASK_FOCUSED
          : '*'.repeat(maskValue.length)
      }
      maskChar={shouldShowFocusedMask ? MASK_CHAR_FOCUSED : MASK_CHAR_UNFOCUSED}
      onChange={(e) => {
        const maskValue = e.target.value;
        const isEmpty = maskValue === MASK_EMPTY || !maskValue;
        if (shouldSaveInvalidDate && !isEmpty) {
          field.onChange(new Date(maskValue));
        } else if (!isValidDate(maskValue)) {
          field.onChange(null);
        }

        setMaskValue(maskValue);
      }}
      value={maskValue}
      ref={field.ref}
    >
      {(props) => (
        <InputField
          label={label}
          placeholder={placeholder || 'yyyy-mm-dd'}
          errorMessage={errorMessage}
          isInvalid={Boolean(errorMessage)}
          rightElementProps={{
            width: tooltip ? '100px' : '60px',
          }}
          rightElement={
            <>
              {!isRequired && field.value && (
                <Icon
                  opacity="0"
                  as={CloseIcon}
                  data-testid="clear-date"
                  width="20px"
                  height="20px"
                  color="iconBase"
                  _hover={{
                    color: 'M.70',
                  }}
                  cursor="pointer"
                  onClick={() => {
                    field.onChange(null);
                    setMaskValue('');
                  }}
                />
              )}
              <Icon
                as={CalendarIconSvg}
                ref={iconRef}
                ml="auto"
                w={5}
                h={5}
                mr={tooltip ? 0 : 3}
                color="iconBase"
                _hover={{
                  color: 'M.70',
                }}
                cursor="pointer"
                onClick={setIsOpen}
                data-testid="datepicker-icon"
              />
              {tooltip && <HelpTooltip label={tooltip} />}
            </>
          }
          data-testid={dataTestId || name}
          {...props}
        />
      )}
    </InputMask>
  );
};
